/**
 * Background style utilities for consistent styling across the app
 * Performance optimized with memoization cache
 */

// Cache for memoized style objects
const styleCache = new Map();

// Pre-defined constant style properties (avoid object recreation)
const BASE_STYLES = {
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
};

const FULL_SCREEN_CONSTANTS = {
  ...BASE_STYLES,
  backgroundPosition: "center center",
  backgroundColor: "#fafafa",
  minHeight: "100vh",
};

const BANNER_CONSTANTS = {
  ...BASE_STYLES,
  backgroundPosition: "center",
};

const CARD_CONSTANTS = {
  ...BASE_STYLES,
  backgroundPosition: "center",
  backgroundColor: "#fafafa",
};

/**
 * Get inline background styles for full-screen sections
 * Performance optimized with memoization
 * @param {string} backgroundImage - The background image URL
 * @returns {object} Style object for inline styles
 */
export const getFullScreenBackgroundStyle = (backgroundImage) => {
  const cacheKey = `full-${backgroundImage}`;

  if (!styleCache.has(cacheKey)) {
    const style = { ...FULL_SCREEN_CONSTANTS };
    if (backgroundImage) {
      style.backgroundImage = `url(${backgroundImage})`;
    }
    styleCache.set(cacheKey, style);
  }

  return styleCache.get(cacheKey);
};

/**
 * Get inline background styles for banner sections
 * Performance optimized with memoization
 * @param {string} backgroundImage - The background image URL
 * @returns {object} Style object for inline styles
 */
export const getBannerBackgroundStyle = (backgroundImage) => {
  const cacheKey = `banner-${backgroundImage}`;

  if (!styleCache.has(cacheKey)) {
    const style = { ...BANNER_CONSTANTS };
    if (backgroundImage) {
      style.backgroundImage = `url(${backgroundImage})`;
    }
    styleCache.set(cacheKey, style);
  }

  return styleCache.get(cacheKey);
};

/**
 * Get inline background styles for cards/sections
 * Performance optimized with memoization
 * @param {string} backgroundImage - The background image URL
 * @returns {object} Style object for inline styles
 */
export const getCardBackgroundStyle = (backgroundImage) => {
  const cacheKey = `card-${backgroundImage}`;

  if (!styleCache.has(cacheKey)) {
    const style = { ...CARD_CONSTANTS };
    if (backgroundImage) {
      style.backgroundImage = `url(${backgroundImage})`;
    }
    styleCache.set(cacheKey, style);
  }

  return styleCache.get(cacheKey);
};

/**
 * Get CSS class string for background
 * Useful when you want to use both className and inline styles
 * @returns {string} CSS classes
 */
export const getBackgroundClasses = () => "bg-cover bg-center bg-no-repeat";

/**
 * Clear style cache (useful for testing or memory management)
 */
export const clearStyleCache = () => {
  styleCache.clear();
};

/**
 * Get banner border color based on coverImage theme
 * @param {string} coverImage - The cover image key from profile theme
 * @returns {string} Border color hex code
 */
export const getBannerBorderColor = (coverImage) => {
  switch (coverImage) {
    case "foody":
      return "#e6d4b8"; // Current border color
    case "mobile":
      return "#DDDDDDFF"; // Light grey
    case "cream":
      return "#ffc0cb"; // Light pink
    case "noBackground":
      return "#FFFCFDFF"; // Light pink
    default:
      return "#e6d4b8"; // Default to current color
  }
};

/**
 * Get navbar background color based on coverImage theme
 * @param {string} coverImage - The cover image key from profile theme
 * @returns {string} Background color hex code with opacity
 */
export const getNavbarBackgroundColor = (coverImage) => {
  switch (coverImage) {
    case "foody":
      return "rgba(255, 248, 230)"; // Current foody background (warm cream)
    case "mobile":
      return "#f3f3f3"; // Light grey matching mobile background
    case "cream":
      return "#F3EBF2FF"; // Soft cream/pink tint matching cream background
    case "noBackground":
      return "#7C4747EF";
    default:
      return "rgba(255, 248, 230)"; // Default to foody background
  }
};
